import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trash2, CreditCard, Smartphone, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, eur, formatError } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";

export function redirectToRedsys(data) {
  const form = document.createElement("form");
  form.method = "POST";
  form.action = data.gateway;
  Object.entries(data.fields).forEach(([name, value]) => {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = String(value);
    form.appendChild(input);
  });
  document.body.appendChild(form);
  form.submit();
}

export default function Checkout() {
  const { items, setQty, remove, total, clear } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState({ name: user?.name || "", email: user?.email || "", phone: user?.phone || "" });
  const [method, setMethod] = useState("card");
  const [busy, setBusy] = useState(false);

  const pay = async () => {
    if (!customer.name || !customer.email) {
      toast.error("Completa tu nombre y email");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/payments/create", {
        kind: "shop",
        method,
        items: items.map((i) => ({ product_id: i.id, qty: i.qty })),
        customer,
      });
      clear();
      redirectToRedsys(data);
    } catch (e) {
      toast.error(formatError(e));
      setBusy(false);
    }
  };

  if (items.length === 0)
    return (
      <div className="max-w-3xl mx-auto px-5 py-32 text-center" data-testid="empty-cart">
        <h1 className="text-3xl text-white font-light">Tu carrito está vacío</h1>
        <p className="mt-4 text-white/50 font-light">Explora nuestras experiencias y equipamiento.</p>
        <button onClick={() => navigate("/tienda")} className="btn-gold-solid mt-8" data-testid="go-shop-btn">Ir a la Tienda</button>
      </div>
    );

  return (
    <div className="max-w-6xl mx-auto px-5 lg:px-8 py-14 lg:py-20" data-testid="checkout-page">
      <p className="overline-gold mb-3">Checkout</p>
      <h1 className="text-3xl lg:text-5xl text-white font-light tracking-tight">Finalizar <span className="text-gold italic">Compra</span></h1>

      <div className="mt-12 grid grid-cols-1 lg:grid-cols-5 gap-12">
        <div className="lg:col-span-3 space-y-4">
          {items.map((i) => (
            <div key={i.id} className="card-dark p-4 flex items-center gap-4" data-testid={`cart-item-${i.id}`}>
              <img src={i.image} alt={i.name} className="w-20 h-20 object-cover" />
              <div className="flex-1">
                <p className="text-white font-medium">{i.name}</p>
                <p className="text-sand text-sm mt-1">{eur(i.price)}</p>
              </div>
              <div className="flex items-center border border-white/15">
                <button onClick={() => setQty(i.id, i.qty - 1)} className="px-3 py-2 text-white/70 hover:text-gold" data-testid={`cart-minus-${i.id}`}>−</button>
                <span className="px-3 text-white text-sm">{i.qty}</span>
                <button onClick={() => setQty(i.id, i.qty + 1)} className="px-3 py-2 text-white/70 hover:text-gold" data-testid={`cart-plus-${i.id}`}>+</button>
              </div>
              <button onClick={() => remove(i.id)} className="text-white/40 hover:text-red-400 transition-colors" data-testid={`cart-remove-${i.id}`}>
                <Trash2 size={17} strokeWidth={1.5} />
              </button>
            </div>
          ))}
        </div>

        <div className="lg:col-span-2">
          <div className="card-dark p-7 space-y-5" data-testid="checkout-summary">
            <h2 className="text-xl text-white font-medium">Tu pedido</h2>
            <div className="space-y-3">
              <input className="input-dark" placeholder="Nombre completo" value={customer.name}
                onChange={(e) => setCustomer({ ...customer, name: e.target.value })} data-testid="checkout-name" />
              <input className="input-dark" type="email" placeholder="Email" value={customer.email}
                onChange={(e) => setCustomer({ ...customer, email: e.target.value })} data-testid="checkout-email" />
              <input className="input-dark" placeholder="Teléfono (opcional)" value={customer.phone}
                onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} data-testid="checkout-phone" />
            </div>
            <div className="border-t border-white/10 pt-4 flex items-center justify-between">
              <span className="text-white/60 text-sm uppercase tracking-[0.15em]">Total</span>
              <span className="font-heading text-3xl text-gold" data-testid="checkout-total">{eur(total)}</span>
            </div>
            <div className="space-y-3">
              <p className="text-xs uppercase tracking-[0.15em] text-white/40">Método de pago</p>
              <div className="grid grid-cols-2 gap-3">
                <button onClick={() => setMethod("card")}
                  className={`border p-4 flex flex-col items-center gap-2 transition-colors duration-200 ${method === "card" ? "border-gold text-gold" : "border-white/15 text-white/50"}`}
                  data-testid="pay-method-card">
                  <CreditCard size={20} strokeWidth={1.5} />
                  <span className="text-[10px] uppercase tracking-[0.15em]">Tarjeta</span>
                </button>
                <button onClick={() => setMethod("bizum")}
                  className={`border p-4 flex flex-col items-center gap-2 transition-colors duration-200 ${method === "bizum" ? "border-gold text-gold" : "border-white/15 text-white/50"}`}
                  data-testid="pay-method-bizum">
                  <Smartphone size={20} strokeWidth={1.5} />
                  <span className="text-[10px] uppercase tracking-[0.15em]">Bizum</span>
                </button>
              </div>
              {method === "bizum" && total > 10 && (
                <p className="text-amber-400/80 text-xs" data-testid="bizum-test-note">
                  Nota: en el entorno de pruebas de Redsys, Bizum admite hasta 10 €. Para este importe usa tarjeta.
                </p>
              )}
            </div>
            <button onClick={pay} disabled={busy} className="btn-gold-solid w-full disabled:opacity-40" data-testid="pay-button">
              {busy ? "Conectando con el TPV..." : `Pagar ${eur(total)}`}
            </button>
            <p className="flex items-center justify-center gap-2 text-[10px] text-white/35 uppercase tracking-[0.15em]">
              <ShieldCheck size={13} /> Pago seguro vía Redsys (modo pruebas)
            </p>
          </div>
          <Link to="/tienda" className="block text-center mt-5 text-white/40 hover:text-gold text-xs uppercase tracking-[0.15em] transition-colors" data-testid="continue-shopping">
            Seguir comprando
          </Link>
        </div>
      </div>
    </div>
  );
}
